import React, { useState, useEffect } from "react";
import { X, Eye, Bell, ChevronDown, Loader2 } from "lucide-react";
import AnalysisDetailModal from "./AnalysisDetailModal";
import { adminService } from "../services/adminService";

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// ── Analysis History Modal ────────────────────────────────────────────────────
const AnalysisHistoryModal = ({ user, onClose }) => {
  const [rows,          setRows]          = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [viewItem,      setViewItem]      = useState(null);
  const [viewLoadingId, setViewLoadingId] = useState(null);
  const [viewError,     setViewError]     = useState(null);

  const handleView = async (row) => {
    const assetId = row.asset_id ?? row.assetId ?? row.id;
    setViewLoadingId(assetId);
    try {
      const res   = await adminService.getAssetReport(assetId);
      const d     = res?.data ?? res;
      const asset = d?.asset  ?? {};
      const rj    = d?.report?.report_json ?? {};
      const pa    = rj?.privacy_analysis   ?? {};
      const scores = Object.entries(pa).map(([label, v]) => ({ label, score: v.score ?? 0, indicator: v.indicator, summary: v.summary }));
      const avg    = scores.length ? (scores.reduce((s, x) => s + x.score, 0) / scores.length) : 0;
      setViewItem({
        status:           asset.analysis_status ?? "complete",
        title:            asset.software_name   ?? row.software_name ?? "",
        analyzedAt:       formatDate(d?.report?.created_at ?? row.created_at),
        privacyUrl:       asset.privacy_url,
        scores,
        overallScore:     parseFloat(avg.toFixed(1)),
        summary:          rj.summary,
        executiveSummary: rj.executive_summary,
        toolSummary:      rj.tool_summary,
      });
    } catch (err) {
      setViewError(err?.message || "No report found for this asset.");
    } finally {
      setViewLoadingId(null);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    setLoading(true);
    adminService.getUserAnalysisHistory(user.id)
      .then((res) => {
        if (cancelled) return;
        const raw = Array.isArray(res)                   ? res
                  : Array.isArray(res?.data)             ? res.data
                  : Array.isArray(res?.data?.history)    ? res.data.history
                  : Array.isArray(res?.data?.analyses)   ? res.data.analyses
                  : [];
        setRows(raw);
        setError(null);
      })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user?.id]);

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col border-2 border-blue-400">
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between flex-shrink-0">
            <div>
              <h2 className="text-lg font-bold text-brand-text">Analysis History</h2>
              <p className="text-xs text-brand-muted mt-0.5">
                {user?.name || "User"} - All analysis by month
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 text-brand-muted hover:bg-gray-50 transition-colors flex-shrink-0 mt-0.5"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="overflow-y-auto flex-1 px-4 py-2">

            {loading && (
              <div className="flex items-center justify-center py-16 gap-2 text-brand-muted">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Loading history…</span>
              </div>
            )}

            {!loading && error && (
              <p className="text-center text-sm text-danger py-10">{error}</p>
            )}

            {!loading && !error && rows.length === 0 && (
              <p className="text-center text-sm text-brand-muted py-10">No analysis history found.</p>
            )}

            {viewError && (
              <div className="mx-1 mb-3 flex items-center justify-between gap-3 bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-2.5 rounded-xl">
                <span>{viewError}</span>
                <button onClick={() => setViewError(null)} className="text-red-400 hover:text-red-600 flex-shrink-0"><X className="w-4 h-4" /></button>
              </div>
            )}

            {!loading && !error && rows.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="w-12 px-5 py-4" />
                    <th className="px-5 py-4 text-left font-semibold text-brand-muted">Company</th>
                    <th className="px-5 py-4 text-left font-semibold text-brand-muted">Date</th>
                    <th className="px-5 py-4 text-left font-semibold text-brand-muted">Status</th>
                    <th className="px-5 py-4 text-left font-semibold text-brand-muted">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    const status        = (row.asset?.analysis_status || row.status || "complete").toLowerCase();
                    const policyChanged = !!(row.policy_changed ?? row.policyChanged);
                    const company       = row.software_name ?? row.company_name ?? row.company ?? row.vendor_name ?? "—";
                    const date          = formatDate(row.created_at ?? row.createdAt ?? row.date);
                    const hasReport     = !!(row.revision_id ?? row.revision?.id);

                    return (
                      <tr key={row.id ?? i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="px-5 py-4">
                          <ChevronDown className="w-4 h-4 text-brand-muted" />
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-brand-text">{company}</span>
                            {policyChanged && (
                              <span className="inline-flex items-center gap-1 bg-orange-50 text-orange-600 text-xs font-medium px-2 py-0.5 rounded-full border border-orange-200">
                                <Bell className="w-3 h-3" />
                                Policy Changed
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="px-5 py-4 text-brand-muted whitespace-nowrap">{date}</td>

                        <td className="px-5 py-4">
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${
                            status === "complete" || status === "completed"
                              ? "bg-emerald-50 text-emerald-600"
                              : status === "pending"
                              ? "bg-amber-50 text-amber-600"
                              : "bg-slate-100 text-slate-500"
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full inline-block ${
                              status === "complete" || status === "completed" ? "bg-emerald-500"
                              : status === "pending" ? "bg-amber-500" : "bg-slate-400"
                            }`} />
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          {hasReport ? (
                            <button
                              onClick={() => { setViewError(null); handleView(row); }}
                              disabled={!!viewLoadingId}
                              className="inline-flex items-center gap-1.5 text-blue-600 font-medium hover:text-blue-700 transition-colors disabled:opacity-50"
                            >
                              {viewLoadingId === (row.asset_id ?? row.assetId ?? row.id)
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Eye className="w-4 h-4" />}
                              View
                            </button>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-brand-muted text-sm cursor-not-allowed" title="No report available">
                              <Eye className="w-4 h-4 opacity-40" />
                              <span className="opacity-40">View</span>
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Detail modal — layered on top */}
      {viewItem && (
        <AnalysisDetailModal
          analysis={viewItem}
          user={user}
          onClose={() => setViewItem(null)}
        />
      )}
    </>
  );
};

export default AnalysisHistoryModal;
